import type { CSSProperties } from "react";
import { Input } from "@design-system/Input/Input";
import { TextArea } from "@design-system/TextArea/TextArea";
import type { TextAreaSize } from "@design-system/TextArea/TextArea.types";
import { textAreaSizes } from "./TextAreaPlaygroundControls";
import { VariantGrid, VariantItem } from "./playgroundControls";

/**
 * @purpose Every drawn Figma state, plus the height modes, so the multiline
 * field can be compared with Input row for row.
 */

const sizeCompareStyle: CSSProperties = {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
};

const sizeCompareFieldStyle: CSSProperties = {
    flex: 1,
    minWidth: 0,
};

const autoGrowSample = [
    "First paragraph of a memo that runs long enough to wrap onto another line.",
    "",
    "Second paragraph keeps going so the field grows past two rows.",
    "",
    "Third paragraph is here so maxRows can clamp and the control starts to scroll.",
].join("\n");

const resizableSample =
    "A memo long enough to wrap, so every visible row runs right up to the resize grip in the corner of the field.";

const SizeBesideInput = ({ size }: { size: TextAreaSize }) => (
    <div style={sizeCompareStyle}>
        <div style={sizeCompareFieldStyle}>
            <TextArea size={size} rows={1} defaultValue="Input" />
        </div>
        <div style={sizeCompareFieldStyle}>
            <Input size={size} defaultValue="Input" />
        </div>
    </div>
);

export const TextAreaMatrix = () => (
    <>
        <VariantGrid title="Sizes beside Input" columns="full">
            {textAreaSizes.map((size) => (
                <VariantItem key={size} label={`size="${size}"`}>
                    <SizeBesideInput size={size} />
                </VariantItem>
            ))}
        </VariantGrid>
        <VariantGrid title="States (md)" columns="wide">
            <VariantItem label="empty">
                <TextArea size="md" placeholder="Placeholder" />
            </VariantItem>
            <VariantItem label="filled">
                <TextArea size="md" defaultValue="Text area" />
            </VariantItem>
            <VariantItem label="hover">
                <TextArea
                    size="md"
                    defaultValue="Text area"
                    data-forced-state="hover"
                />
            </VariantItem>
            <VariantItem label="focus">
                <TextArea
                    size="md"
                    defaultValue="Text area"
                    data-forced-state="focus"
                />
            </VariantItem>
            <VariantItem label="invalid empty">
                <TextArea size="md" placeholder="Placeholder" invalid />
            </VariantItem>
            <VariantItem label="invalid filled">
                <TextArea size="md" defaultValue="Text area" invalid />
            </VariantItem>
            <VariantItem label="readOnly">
                <TextArea size="md" defaultValue="Text area" readOnly />
            </VariantItem>
            <VariantItem label="disabled">
                <TextArea size="md" defaultValue="Text area" disabled />
            </VariantItem>
        </VariantGrid>
        <VariantGrid title="Resizable states (md)" columns="three">
            <VariantItem label="rest">
                <TextArea
                    size="md"
                    resizable
                    rows={3}
                    defaultValue={resizableSample}
                />
            </VariantItem>
            <VariantItem label="hover">
                <TextArea
                    size="md"
                    resizable
                    rows={3}
                    defaultValue={resizableSample}
                    data-forced-state="hover"
                />
            </VariantItem>
            <VariantItem label="focus">
                <TextArea
                    size="md"
                    resizable
                    rows={3}
                    defaultValue={resizableSample}
                    data-forced-state="focus"
                />
            </VariantItem>
            <VariantItem label="invalid">
                <TextArea
                    size="md"
                    resizable
                    rows={3}
                    defaultValue={resizableSample}
                    invalid
                />
            </VariantItem>
            <VariantItem label="invalid hover">
                <TextArea
                    size="md"
                    resizable
                    rows={3}
                    defaultValue={resizableSample}
                    invalid
                    data-forced-state="hover"
                />
            </VariantItem>
            <VariantItem label="readOnly">
                <TextArea
                    size="md"
                    resizable
                    rows={3}
                    defaultValue={resizableSample}
                    readOnly
                />
            </VariantItem>
        </VariantGrid>
        <VariantGrid title="Height modes" columns="wide">
            <VariantItem label="rows={3}">
                <TextArea size="md" rows={3} defaultValue="Three rows" />
            </VariantItem>
            <VariantItem label="autoGrow">
                <TextArea
                    size="md"
                    autoGrow
                    minRows={2}
                    maxRows={5}
                    defaultValue={autoGrowSample}
                />
            </VariantItem>
            <VariantItem label="resizable">
                <TextArea
                    size="md"
                    resizable
                    rows={3}
                    defaultValue="Drag the grip"
                />
            </VariantItem>
        </VariantGrid>
    </>
);
